import React from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { IconLogout2 } from '@tabler/icons-react';
import { navTransactionLabel, useNavTransactions } from 'helpers/useNavTransactions';

import { useObservability } from '../../helpers/observability';
import { navTransactionIcon } from './navTransactionIcon';

interface AccountMenuProps {
  isExpanded: boolean;
  isLoggedIn: boolean;
}

const HELP_CENTER_URL = 'https://help.opendoor.com';

// @tabler icons accept any CSS color string; the atoms theme defines the token var.
const ICON_COLOR = 'var(--color-od-content-primary)';

/**
 * The signed-in dropdown lists the customer's active transactions above a static
 * footer (Account, Help center, Sign out).
 */
const AccountMenu = ({ isExpanded, isLoggedIn }: AccountMenuProps) => {
  if (!isLoggedIn) {
    return (
      <PrimaryNavSubmenu isExpanded={isExpanded} isLoggedIn={false}>
        <PrimaryNavItem>
          <MenuItem href={HELP_CENTER_URL} ariaLabel="Help center">
            Help center
          </MenuItem>
        </PrimaryNavItem>
      </PrimaryNavSubmenu>
    );
  }

  return (
    <PrimaryNavSubmenu isExpanded={isExpanded} isLoggedIn>
      <ActiveTransactionItems />
      <AccountMenuFooter />
    </PrimaryNavSubmenu>
  );
};

/** The customer's active purchases and sales. */
const ActiveTransactionItems: React.FC = () => {
  const transactions = useNavTransactions();

  return (
    <>
      {transactions.map((transaction) => {
        const Icon = navTransactionIcon(transaction.type);
        const label = navTransactionLabel(transaction);
        return (
          <PrimaryNavItem key={transaction.id}>
            <MenuItem
              href={transaction.href}
              ariaLabel={label}
              icon={<Icon size={20} stroke={1.5} color={ICON_COLOR} />}
            >
              {label}
            </MenuItem>
          </PrimaryNavItem>
        );
      })}
    </>
  );
};

/** Account, Help center, and Sign out — shown beneath the active transactions. */
const AccountMenuFooter: React.FC = () => {
  const { trackEvent } = useObservability();
  const { logout } = useAuth();

  const handleSignOutClick = () => {
    // TODO: remove categoryOverride
    // observability is initialized with 'cosmos' as the category for all events
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    logout();
  };

  return (
    <>
      <PrimaryNavItem>
        <MenuItem
          ariaLabel="Account"
          // TODO: remove categoryOverride
          // observability is initialized with 'cosmos' as the category for all events
          onClick={() =>
            trackEvent('cta-click', 'account-link', undefined, { categoryOverride: 'homepage' })
          }
          href="/account"
        >
          Account
        </MenuItem>
      </PrimaryNavItem>
      <Divider />
      <PrimaryNavItem>
        <MenuItem href={HELP_CENTER_URL} ariaLabel="Help center">
          Help center
        </MenuItem>
      </PrimaryNavItem>
      <PrimaryNavItem>
        <MenuButton
          onPress={handleSignOutClick}
          label="Sign out"
          icon={<IconLogout2 size={20} stroke={1.5} color={ICON_COLOR} />}
          analyticsName="cosmos-account-menu-sign-out"
        />
      </PrimaryNavItem>
    </>
  );
};

const PrimaryNavSubmenu: React.FC<{
  isExpanded: boolean;
  isLoggedIn: boolean;
  children: React.ReactNode;
}> = ({ isExpanded, isLoggedIn, children }) => (
  <Col
    {...(!isExpanded ? { inert: true } : {})}
    aria-hidden={!isExpanded}
    className={cn(
      // top-full + translate-y-3 (12px): clears the avatar and covers the gap to
      // the bottom of the header bar (avatar is vertically centered)
      'absolute top-full right-0 z-[1000] translate-y-3 gap-1 overflow-hidden rounded-xl border border-od-border-primary bg-od-bg-primary p-1 transition-[opacity,visibility] duration-200',
      isLoggedIn ? 'min-w-67 max-w-67' : 'min-w-50 max-w-68',
      isExpanded ? 'visible opacity-100' : 'invisible opacity-0',
    )}
  >
    {children}
  </Col>
);

const PrimaryNavItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Col className="w-full">{children}</Col>
);

const Divider = () => <Box role="separator" className="h-px bg-od-border-secondary" />;

interface MenuItemProps {
  href: string;
  children: React.ReactNode;
  ariaLabel: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ href, children, ariaLabel, icon, onClick }) => (
  <Row
    href={href}
    aria-label={ariaLabel}
    onClick={onClick}
    className={cn(
      'w-full cursor-pointer items-center gap-2 rounded-lg hover:bg-od-bg-hover-primary',
      icon ? 'py-2.5 pr-2 pl-2' : 'py-2 pr-3 pl-9',
    )}
  >
    {icon && <Box className="size-5 items-center justify-center">{icon}</Box>}
    <Text as="span" className="min-w-0 truncate text-body-md text-od-content-primary">
      {children}
    </Text>
  </Row>
);

interface MenuButtonProps {
  onPress: () => void;
  label: string;
  icon?: React.ReactNode;
  analyticsName: string;
}

const MenuButton: React.FC<MenuButtonProps> = ({ onPress, label, icon, analyticsName }) => (
  <Row
    onClick={onPress}
    aria-label={label}
    analyticsName={analyticsName}
    className={cn(
      'w-full cursor-pointer items-center justify-start gap-2 rounded-lg py-2 pr-3 hover:bg-od-bg-hover-primary',
      icon ? 'pl-2' : 'pl-9',
    )}
  >
    {icon && <Box className="size-5 items-center justify-center">{icon}</Box>}
    <Text as="span" className="min-w-0 truncate text-body-md text-od-content-primary">
      {label}
    </Text>
  </Row>
);

export default AccountMenu;
